import { Spin, Input, Collapse, Divider, Space, Button, Layout, Tooltip } from 'antd';
import {
  FileSearchOutlined,
  SearchOutlined,
  PlusOutlined,
  FolderOutlined,
  WechatOutlined,
  FolderFilled,
  FolderOpenOutlined
} from '@ant-design/icons';
import { Empty } from 'antd';
import React, { useEffect, useState } from 'react';
import newConversationImage from '../../../assets/newConversation.png';
import { Mode, useChatState } from '../state/chatState';
import moment from 'moment';

import { Session, CategorizedProjects, ProjectCategory, NodeCategory, GroupedSessionsProject } from 'api/useChat';
import NewDealProjectModal from '../DealConversations/NewDealProjectModal';
import MoreActions from '../MoreActions/MoreActions';
import './SideNavigation.scss';
import SessionList from './SessionList';
import ConversationsIcon from 'assets/square-list.svg';
import juggernautIcon from 'assets/juggernaut.svg';

const { Panel } = Collapse;

const SideNavigation: React.FC<{ mode: Mode }> = ({ mode }) => {
  const {
    state: { currentSessionId, searchValue, isLoading, embeddedWindowState, dialogs },
    setSearchValue,
    selectSession,
    filteredProjects,
    createNewDealProject,
    handleNewConversation,
    openNewDealProjectDialog,
    closeNewDealProjectDialog
  } = useChatState();

  const [activeCategoryKey, setActiveCategoryKey] = useState('');
  const [activeProjectsKey, setActiveProjectsKey] = useState('');

  const handleSessionSelect = async (
    session: Session,
    index: number,
    category: string,
    project: GroupedSessionsProject
  ) => {
    await selectSession(session.id, index, session, category, project);
  };

  const handleCategoryKeyChange = (categoryKey: string | string[]) => {
    const key = categoryKey && Array.isArray(categoryKey) ? categoryKey[0] || '' : categoryKey || '';
    setActiveProjectsKey('');
    setActiveCategoryKey(key);
  };

  const handleProjectsKeyChange = (projectsKey: string | string[]) => {
    const key = projectsKey && Array.isArray(projectsKey) ? projectsKey[0] || '' : projectsKey || '';
    setActiveProjectsKey(key);
  };

  useEffect(() => {
    if (currentSessionId) {
      setActiveCategoryKey(currentSessionId?.category);
      setActiveProjectsKey(currentSessionId?.project?.id || '');
    }
    return () => {
      setSearchValue('');
    };
  }, [currentSessionId]);

  const getCategoryIcon = (category: keyof CategorizedProjects) => {
    switch (category) {
      case ProjectCategory.GENERAL:
        return <img src={ConversationsIcon} alt="Conversations" className="w-[24px] h-[24px]" />;
      case ProjectCategory.DEAL:
        return <FolderFilled className="text-[18px]" />;
      case ProjectCategory.SUPPLIER:
        return <FolderFilled />;
      case ProjectCategory.CLIENT:
        return <FolderFilled />;
      default:
        return <FolderFilled />;
    }
  };

  const renderEmbeddedCategoryPanel = (category: keyof CategorizedProjects) => {
    return (
      <Collapse
        className="py-0"
        accordion={searchValue?.length <= 0}
        ghost
        collapsible="header"
        activeKey={searchValue?.length > 0 ? filteredProjects[category].map((proj) => proj?.id) : activeProjectsKey}
        expandIcon={(panelProps) => (panelProps?.isActive ? <FolderOpenOutlined /> : <FolderOutlined />)}
        onChange={(key) => handleProjectsKeyChange(key)}
      >
        {filteredProjects[category].map((project: GroupedSessionsProject, projectIndex: number) => (
          <Panel
            className="py-0"
            key={project.id}
            header={
              <div className="truncate text-sm font-normal">
                <Tooltip title={project.name}>
                  {project.contract?.salesforceDealName} - {project.name}
                </Tooltip>
                <div className="text-xs text-gray-500">
                  {project.contract?.client.name} | {project.contract?.vendor.name} | {project.contract?.dealType}
                </div>
                <div className="text-xs text-gray-500 mb-1">
                  {moment(project.createdAt).format('MMM D, YYYY hh:mm A')}
                </div>
              </div>
            }
            extra={
              searchValue?.length <= 0 && (
                <MoreActions
                  page="side-navigation"
                  project={project}
                  projectIndex={projectIndex}
                  category={category}
                  nodeCategory={NodeCategory.PROJECT}
                />
              )
            }
          >
            {Object.keys(project?.chatSessions).length > 0 ? (
              <SessionList
                mode={mode}
                sessions={Object.entries(project.chatSessions) as [string, Session[]][]}
                currentSessionId={currentSessionId}
                projectIndex={projectIndex}
                category={category}
                project={project}
                onSessionSelect={handleSessionSelect}
                searchValue={searchValue}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'No Conversations'} />
            )}
          </Panel>
        ))}
      </Collapse>
    );
  };

  const renderCategoryPanel = (category: keyof CategorizedProjects) => {
    if (![ProjectCategory.GENERAL, ProjectCategory.DEAL].includes(category as ProjectCategory)) {
      return null;
    }
    const showNewProjectButton = category !== 'GENERAL';

    return (
      <Panel
        className={'py-0 sticky top-0 bg-[#f5f5f5] z-10'}
        header={
          <div className="text-base font-semibold">
            <Space>
              {getCategoryIcon(category)}
              {category}
            </Space>
          </div>
        }
        key={category}
        showArrow={false}
        extra={
          searchValue?.length <= 0 && showNewProjectButton ? (
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={() => openNewDealProjectDialog()}
              title="Create New Project"
            />
          ) : null
        }
      >
        <div className="max-h-[calc(100vh-552px)] overflow-y-auto">
          {filteredProjects[category]?.length > 0 ? (
            category === ProjectCategory.GENERAL ? (
              filteredProjects[category].map((project: GroupedSessionsProject, projectIndex: number) => (
                <SessionList
                  mode={mode}
                  key={project.id}
                  sessions={Object.entries(project.chatSessions) as [string, Session[]][]}
                  currentSessionId={currentSessionId}
                  projectIndex={projectIndex}
                  category={category}
                  project={project}
                  onSessionSelect={handleSessionSelect}
                  searchValue={searchValue}
                />
              ))
            ) : (
              renderEmbeddedCategoryPanel(category)
            )
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                searchValue?.length <= 0 && showNewProjectButton ? (
                  <>
                    <div>No Conversations</div>
                    <Button type="link" icon={<PlusOutlined />} onClick={() => openNewDealProjectDialog()}>
                      Start New
                    </Button>
                  </>
                ) : (
                  'No Conversations'
                )
              }
            />
          )}
        </div>
      </Panel>
    );
  };

  return (
    <>
      {mode === 'embedded' || mode === 'horizontal' ? (
        <Layout className="projects-sidenavigation embeded-container">
          <Layout.Content className="overflow-y-auto max-h-[500px]">
            {isLoading.isSessionLoading ? (
              <div className="flex justify-center items-center h-[400px]">
                <Spin />
              </div>
            ) : (
              renderEmbeddedCategoryPanel(embeddedWindowState.type)
            )}
          </Layout.Content>
          <Layout.Footer className="flex justify-center items-center p-2 bg-white border-t border-gray-200">
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={() => createNewDealProject(embeddedWindowState.data.ContractId || 0)}
              disabled={isLoading.isMessageLoading}
            >
              Add New Project
            </Button>
          </Layout.Footer>
        </Layout>
      ) : (
        <>
          <div className="flex items-center p-4">
            <img src={juggernautIcon} alt="Juggernaut" width={'40px'} height={'40px'} />
            <span className="ml-2 font-bold text-lg">Juggernaut</span>
            <div className="ml-auto" title="New Conversation">
              <img
                src={newConversationImage}
                alt="new conversation"
                className="w-10 h-10 cursor-pointer"
                onClick={() => handleNewConversation()}
              />
            </div>
          </div>

          <div className="p-4">
            <Input
              className="w-full rounded-lg"
              size="large"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search conversation"
              allowClear
              suffix={!searchValue && <SearchOutlined className="text-gray-400" />}
            />
          </div>

          <div className="projects-sidenavigation p-4 overflow-y-auto h-[calc(100%-160px)]">
            {isLoading.isSessionLoading ? (
              <div className="flex justify-center items-center h-full">
                <Spin />
              </div>
            ) : Object.keys(filteredProjects).length > 0 ? (
              <Collapse
                className="projects py-0"
                accordion={searchValue?.length <= 0}
                ghost
                collapsible="header"
                activeKey={searchValue?.length > 0 ? Object.keys(filteredProjects) : activeCategoryKey}
                onChange={(key) => handleCategoryKeyChange(key)}
              >
                {(Object.keys(filteredProjects) as Array<keyof CategorizedProjects>).map((category) => (
                  <React.Fragment key={category}>
                    {renderCategoryPanel(category)}
                    {category !==
                      (Object.keys(filteredProjects) as Array<keyof CategorizedProjects>)[
                        Object.keys(filteredProjects).length - 1
                      ] && <Divider />}
                  </React.Fragment>
                ))}
              </Collapse>
            ) : (
              <Empty description="No matching conversations" className="mt-4" />
            )}
          </div>
        </>
      )}

      <NewDealProjectModal
        open={dialogs.newDealProject.isOpen}
        onCancel={closeNewDealProjectDialog}
        onSubmit={() => {
          closeNewDealProjectDialog();
        }}
      />
    </>
  );
};

export default SideNavigation;
